import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import classes from "../Pages/Patient/PatientForm.module.css";
import { FaUserEdit, FaIdCard, FaAddressCard, FaPhoneAlt, FaMapMarkerAlt, FaSave, FaTimes } from 'react-icons/fa';
import { getAuthToken } from "../../util/auth";
import { getApiChara } from "../../util/getApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const PatientActionModal = ({ show, onHide, patientData, mode = "ADD", onSuccess }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        patientId: "",
        firstName: "",
        middleName: "",
        lastName: "",
        mobile: "",
        nickName: "",
        gender: "male",
        dob: "",
        age: "",
        address: "",
        maritalStatus: "single",
        religion: "",
        caste: "",
        panCardNo: "",
        adharCardNo: "",
        occupation: "",
        identificationMark: "",
        emailId: "",
        alternateMobileNum: "",
        city: "",
        pincode: "",
        referredBy: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (show && patientData && mode === "EDIT") {
            setFormData({
                ...patientData,
                patientId: patientData.patientId,
                dob: patientData.dob ? patientData.dob.split("T")[0] : "",
            });
        } else if (show && mode === "ADD") {
            setFormData({
                patientId: "",
                firstName: "",
                middleName: "",
                lastName: "",
                mobile: "",
                nickName: "",
                gender: "male",
                dob: "",
                age: "",
                address: "",
                maritalStatus: "single",
                religion: "",
                caste: "",
                panCardNo: "",
                adharCardNo: "",
                occupation: "",
                identificationMark: "",
                emailId: "",
                alternateMobileNum: "",
                city: "",
                pincode: "",
                referredBy: "",
            });
        }
    }, [show, patientData, mode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const token = getAuthToken();
        const apiChar = getApiChara();
        const apiUrl = mode === "ADD"
            ? `${apiChar}/patient/addPatient`
            : `${apiChar}/patient/updatePatient`;

        const method = mode === "ADD" ? "POST" : "PUT";

        const submissionData = {
            ...formData,
            dob: formData.dob || null
        };

        try {
            const response = await fetch(apiUrl, {
                method: method,
                body: JSON.stringify(submissionData),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            let resData = {};
            const resText = await response.text();
            try {
                resData = JSON.parse(resText);
            } catch (e) {
                if (resText.includes("Patient already Admitted")) {
                    toast.error(t("Patient already exists in the system."));
                    setIsSubmitting(false);
                    return;
                }
                resData.message = resText;
            }

            if (!response.ok) {
                throw new Error(resData.message || t(`Could not {{action}} patient`, { action: mode === "ADD" ? "save" : "update" }));
            }

            if (resData.errorMsg) {
                toast.error(resData.errorMsg);
            } else {
                toast.success(t(`Patient {{action}} successfully!`, { action: mode === "ADD" ? "added" : "updated" }));
                if (onSuccess) onSuccess(resData);
                onHide();
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable backdrop="static">
            <Modal.Header closeButton style={{ backgroundColor: "var(--primary-color)", color: "white" }}>
                <Modal.Title style={{ fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    <FaUserEdit className="me-2" />
                    {mode === "ADD" ? t("Register Patient") : t("Edit Patient Details")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className={classes.cardview} style={{ boxShadow: 'none', border: 'none', borderRadius: '0', maxWidth: '100%', marginBottom: '0' }}>
                    <form onSubmit={handleSubmit} className={classes.form}>

                        <div className={classes.sectionHeader}>
                            <FaIdCard /> {t("Personal Information")}
                        </div>
                        <div className="row">
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="firstName">{t("First Name")} <span className={classes.reqSign}>*</span></label>
                                <input id="firstName" type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="middleName">{t("Middle Name")}</label>
                                <input id="middleName" type="text" name="middleName" value={formData.middleName} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="lastName">{t("Last Name")} <span className={classes.reqSign}>*</span></label>
                                <input id="lastName" type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="nickName">{t("Nick Name")}</label>
                                <input id="nickName" type="text" name="nickName" value={formData.nickName} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="gender">{t("Gender")} <span className={classes.reqSign}>*</span></label>
                                <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required>
                                    <option value="male">{t("Male")}</option>
                                    <option value="female">{t("Female")}</option>
                                    <option value="other">{t("Other")}</option>
                                </select>
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="dob">{t("Date of Birth")}</label>
                                <input id="dob" type="date" name="dob" value={formData.dob} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="age">{t("Age (Years)")}</label>
                                <input id="age" type="number" name="age" value={formData.age} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="maritalStatus">{t("Marital Status")}</label>
                                <select id="maritalStatus" name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
                                    <option value="single">{t("Single")}</option>
                                    <option value="married">{t("Married")}</option>
                                    <option value="divorced">{t("Divorced")}</option>
                                    <option value="widowed">{t("Widowed")}</option>
                                </select>
                            </div>
                        </div>

                        <div className={classes.sectionHeader}>
                            <FaAddressCard /> {t("Identity & Background")}
                        </div>
                        <div className="row">
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="religion">{t("Religion")}</label>
                                <input id="religion" type="text" name="religion" value={formData.religion} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="caste">{t("Caste")}</label>
                                <input id="caste" type="text" name="caste" value={formData.caste} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="occupation">{t("Occupation")}</label>
                                <input id="occupation" type="text" name="occupation" value={formData.occupation} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="panCardNo">{t("Pan Card No")}</label>
                                <input id="panCardNo" type="text" name="panCardNo" value={formData.panCardNo} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="adharCardNo">{t("Aadhar Card No")}</label>
                                <input id="adharCardNo" type="text" name="adharCardNo" value={formData.adharCardNo} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="identificationMark">{t("Identification Mark")}</label>
                                <input id="identificationMark" type="text" name="identificationMark" value={formData.identificationMark} onChange={handleChange} />
                            </div>
                        </div>

                        <div className={classes.sectionHeader}>
                            <FaPhoneAlt /> {t("Contact Information")}
                        </div>
                        <div className="row">
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="mobile">{t("Mobile Number")}</label>
                                <input id="mobile" type="tel" name="mobile" value={formData.mobile} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="alternateMobileNum">{t("Alt Mobile No")}</label>
                                <input id="alternateMobileNum" type="tel" name="alternateMobileNum" value={formData.alternateMobileNum} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="emailId">{t("Email")}</label>
                                <input id="emailId" type="email" name="emailId" value={formData.emailId} onChange={handleChange} />
                            </div>
                        </div>

                        <div className={classes.sectionHeader}>
                            <FaMapMarkerAlt /> {t("Address & Reference")}
                        </div>
                        <div className="row">
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="address">{t("Address")} <span className={classes.reqSign}>*</span></label>
                                <input id="address" type="text" name="address" value={formData.address} onChange={handleChange} required />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="city">{t("City")}</label>
                                <input id="city" type="text" name="city" value={formData.city} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="pincode">{t("Pincode")}</label>
                                <input id="pincode" type="text" name="pincode" value={formData.pincode} onChange={handleChange} />
                            </div>
                            <div className="col-md-4 col-6">
                                <label className={classes.label} htmlFor="referredBy">{t("Referred By")}</label>
                                <input id="referredBy" type="text" name="referredBy" value={formData.referredBy} onChange={handleChange} />
                            </div>
                        </div>

                        <div className={classes.actions}>
                            <button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <Spinner animation="border" size="sm" className="me-2" />
                                ) : (
                                    <FaSave className="me-2" />
                                )}
                                {isSubmitting ? t("Submitting...") : t("Save Details")}
                            </button>
                            <button type="button" onClick={onHide} className={classes.secondaryBtn} disabled={isSubmitting}>
                                <FaTimes className="me-2" /> {t("Cancel")}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default PatientActionModal;
